/**
 * Go Game Persistence Service
 *
 * Auto-saves game state to local storage after each move.
 * Follows ReversiGamePersistence pattern.
 */

import {
  GoGameState,
  GoGameSettings,
  GoDifficulty,
} from '../types';
import { LocalStorageManager } from '../../services/LocalStorageManager';
import { DataSynchronizationService } from '../../services/DataSynchronizationService';

export interface SavedGoGame {
  gameId: string;
  mode: GoGameSettings['mode'];
  gameState: GoGameState;
  metadata: {
    aiDifficulty?: GoDifficulty;
    startedAt: number;
    lastSavedAt: number;
  };
}

export class GoGamePersistence {
  private storage: LocalStorageManager;
  private syncService: DataSynchronizationService | null;
  private readonly STORAGE_KEY = 'go_saved_games';
  private readonly AUTO_SAVE_KEY = 'go_auto_save';

  constructor(
    storage: LocalStorageManager,
    syncService?: DataSynchronizationService
  ) {
    this.storage = storage;
    this.syncService = syncService || null;
  }

  autoSave(
    mode: GoGameSettings['mode'],
    gameState: GoGameState,
    metadata: SavedGoGame['metadata']
  ): boolean {
    try {
      const savedGame: SavedGoGame = {
        gameId: 'auto_save',
        mode,
        gameState,
        metadata: { ...metadata, lastSavedAt: Date.now() },
      };
      localStorage.setItem(this.AUTO_SAVE_KEY, JSON.stringify(savedGame));
      return true;
    } catch (error) {
      console.error('Failed to auto-save go game:', error);
      return false;
    }
  }

  loadAutoSave(): SavedGoGame | null {
    try {
      const data = localStorage.getItem(this.AUTO_SAVE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Failed to load go auto-save:', error);
      return null;
    }
  }

  clearAutoSave(): boolean {
    try {
      localStorage.removeItem(this.AUTO_SAVE_KEY);
      return true;
    } catch (error) {
      console.error('Failed to clear go auto-save:', error);
      return false;
    }
  }

  hasAutoSave(): boolean {
    return this.loadAutoSave() !== null;
  }

  saveGame(
    gameId: string,
    mode: GoGameSettings['mode'],
    gameState: GoGameState,
    metadata: SavedGoGame['metadata']
  ): boolean {
    try {
      const savedGame: SavedGoGame = {
        gameId,
        mode,
        gameState,
        metadata: { ...metadata, lastSavedAt: Date.now() },
      };

      const games = this.loadAllGames();
      const existingIndex = games.findIndex((g) => g.gameId === gameId);

      if (existingIndex >= 0) {
        games[existingIndex] = savedGame;
      } else {
        games.push(savedGame);
      }

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(games));
      return true;
    } catch (error) {
      console.error('Failed to save go game:', error);
      return false;
    }
  }

  loadGame(gameId: string): SavedGoGame | null {
    try {
      const games = this.loadAllGames();
      return games.find((g) => g.gameId === gameId) || null;
    } catch (error) {
      console.error('Failed to load go game:', error);
      return null;
    }
  }

  loadAllGames(): SavedGoGame[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Failed to load go games:', error);
      return [];
    }
  }

  deleteGame(gameId: string): boolean {
    try {
      const games = this.loadAllGames();
      const filtered = games.filter((g) => g.gameId !== gameId);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filtered));
      return true;
    } catch (error) {
      console.error('Failed to delete go game:', error);
      return false;
    }
  }

  clearAllGames(): boolean {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.AUTO_SAVE_KEY);
      return true;
    } catch (error) {
      console.error('Failed to clear all go games:', error);
      return false;
    }
  }

  async syncFromCloud(): Promise<boolean> {
    try {
      if (!this.syncService) return false;
      console.warn('Cloud sync not yet implemented for go games');
      return false;
    } catch (error) {
      console.error('Failed to sync go games from cloud:', error);
      return false;
    }
  }

  async syncToCloud(): Promise<boolean> {
    try {
      if (!this.syncService) return false;
      console.warn('Cloud sync not yet implemented for go games');
      return true;
    } catch (error) {
      console.error('Failed to sync go games to cloud:', error);
      return false;
    }
  }
}
